import { z } from 'zod';
import { SportNameSchema, type SportName } from './profile.js';
import type { LocaleMap } from './geo.js';
import { BadgeCategorySchema, type BadgeCategory } from './profile.js';

/**
 * F37 — Player ↔ Trainer Recommendations: the admin-managed catalogs (D6 badge
 * catalog, D1 review-tag catalog). Both are sport-scoped (NULL = all sports).
 *
 * These types are SHARED by the review surface (tag validation) + the
 * endorsement surface (badge grant). Kept in a separate file so the review +
 * endorsement contracts don't have to cross-import each other.
 */

// ─── Shared constants (frozen in Wave 1) ─────────────────────────────────────

/** Sentinel sport_name for the per-trainer overall aggregate row + platform prior. */
export const OVERALL_SPORT_SENTINEL = '__overall__' as const;

/** D4 — a review is editable within this many days of its last edit. */
export const REVIEW_EDIT_WINDOW_DAYS = 30;

/** D12 — max active badges per (trainer, player) pair. */
export const BADGE_MAX_PER_PAIR = 10;

/** D2 — Bayesian prior weight M (number of "virtual" platform-mean reviews). */
export const BAYESIAN_PRIOR_WEIGHT = 10;

/** Neutral fallback prior mean when the platform has zero reviews. */
export const BAYESIAN_NEUTRAL_PRIOR = 3.0;

/** Max length of a review body (D1). */
export const REVIEW_BODY_MAX = 1000;

/** Max length of an endorsement note (D7). */
export const BADGE_NOTE_MAX = 280;

// ─── Review tag catalog (D1) ─────────────────────────────────────────────────

export type ReviewTagEntry = {
  readonly code: string;
  readonly name: LocaleMap;
  readonly description: LocaleMap | null;
  /** NULL = all sports; else only offerable for the listed sports. */
  readonly sportScope: readonly SportName[] | null;
  readonly active: boolean;
  readonly sortOrder: number;
};

export const UpsertReviewTagInputSchema = z
  .object({
    code: z
      .string()
      .min(1)
      .max(60)
      .regex(/^[a-z0-9_]+$/, 'code must be lower_snake_case'),
    name: z.record(z.string(), z.string().min(1).max(80)),
    description: z.record(z.string(), z.string().max(280)).optional(),
    sportScope: z.array(SportNameSchema).min(1).optional(),
    active: z.boolean().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .strict();
export type UpsertReviewTagInput = z.infer<typeof UpsertReviewTagInputSchema>;

// ─── Badge catalog (D6) ──────────────────────────────────────────────────────

export type BadgeCatalogEntry = {
  readonly id: string;
  readonly code: string;
  readonly name: LocaleMap;
  readonly description: LocaleMap | null;
  /** Resolves to a @bravemove/design-tokens/icons component (D13). */
  readonly iconCode: string;
  readonly badgeCategory: BadgeCategory;
  /** NULL = grantable for all sports; else only the listed sports. */
  readonly sportScope: readonly SportName[] | null;
  readonly active: boolean;
  readonly sortOrder: number;
};

export const UpsertBadgeCatalogInputSchema = z
  .object({
    code: z
      .string()
      .min(1)
      .max(60)
      .regex(/^[a-z0-9_]+$/, 'code must be lower_snake_case'),
    name: z.record(z.string(), z.string().min(1).max(80)),
    description: z.record(z.string(), z.string().max(280)).optional(),
    iconCode: z.string().min(1).max(80),
    badgeCategory: BadgeCategorySchema,
    sportScope: z.array(SportNameSchema).min(1).optional(),
    active: z.boolean().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .strict();
export type UpsertBadgeCatalogInput = z.infer<typeof UpsertBadgeCatalogInputSchema>;
